import styled from '@emotion/styled';

import { isPC } from '../../../../../util/window/getDevice';
import { addComma } from '../../../../shared/services';

import COLOR from '../../../../shared/constant/colorset';

import Alert from '../../../../shared/components/Alert/Alert';

const Title = styled.h3`
  margin-top: ${isPC ? '-17px' : 0};
  margin-bottom: ${isPC ? '20px' : '16px'};
  font-size: 18px;
  font-weight: ${isPC ? 500 : 600};
  line-height: 24px;
  color: ${COLOR.kurlyGray800};
  text-align: left;
  letter-spacing: -0.5px;
`;

const PriceInfo = styled.div`
  text-align: left;
  padding-top: 12px;
  color: ${COLOR.kurlyGray600};
  letter-spacing: 0;
  &:first-of-type {
    margin-top: -1px;
    padding-top: 0;
  }
  &:last-of-type {
    margin-bottom: -10px;
  }
`;

const Price = styled.span<{ lineThrough: boolean; }>`
  font-weight: 500;
  font-size: 16px;
  color: ${COLOR.kurlyGray800};
  line-height: 20px;
  letter-spacing: -0.5px;
  text-decoration: ${ ({ lineThrough }) => lineThrough ? 'line-through' : 'none'};
`;

const PriceRetail = styled(Price)`
  text-decoration: line-through;
`;

const PriceDiscountRate = styled.span`
  padding-right: 4px;
  color: ${COLOR.pointText};
  letter-spacing: -0.5px;
  font-weight: 500;
  font-size: 16px;
  line-height: 20px;
`;

const PriceGuideText = styled.div`
  font-size: 14px;
  font-weight: 400;
  color: ${COLOR.kurlyGray450};
  line-height: 1.36;
  letter-spacing: -0.5px;
  word-break: keep-all;
  margin-top: 6px;
`;

interface Props {
  discountRate: number;
  representativePrice: number;
  kurlyPrice: number | null;
  originalPrice: number | null;
}

export default function PriceInformationAlert({
  discountRate,
  representativePrice,
  kurlyPrice,
  originalPrice,
}: Props) {
  const lineThrough = !!kurlyPrice && !originalPrice;

  const displayKurlyPrice = (representativePrice !== null && !kurlyPrice) ? representativePrice : kurlyPrice;

  const displayRepresentativePrice = ((representativePrice !== null) && !kurlyPrice) ? null : representativePrice;

  const displayDiscountedRate = !displayRepresentativePrice && displayRepresentativePrice === null;

  return Alert({
    title: '',
    contents: (
      <>
        <Title>가격 안내</Title>
        {originalPrice && (
          <PriceInfo>
            <PriceRetail lineThrough>
              {addComma(originalPrice)}원
            </PriceRetail>
            <PriceGuideText>파트너사 제공 권장소비자가 또는 타사 판매 가격으로, 할인 기준이 되는 가격입니다.</PriceGuideText>
          </PriceInfo>
        )}
        {displayKurlyPrice && (
          <PriceInfo>
            {displayDiscountedRate && (
              <PriceDiscountRate>
                {addComma(discountRate)}%
              </PriceDiscountRate>
            )}
            <Price lineThrough={lineThrough}>
              {addComma(displayKurlyPrice)}원
            </Price>
            <PriceGuideText>마켓컬리에서 상시 판매되는 가격입니다.</PriceGuideText>
          </PriceInfo>
        )}
        {(displayRepresentativePrice !== null) && (
          <PriceInfo>
            <Price lineThrough={false}>
              {!!discountRate && (
                <PriceDiscountRate>
                  {addComma(discountRate)}%
                </PriceDiscountRate>
              )}
              {addComma(displayRepresentativePrice)}원
            </Price>
            <PriceGuideText>할인 혜택이 적용된 최종 가격입니다.</PriceGuideText>
          </PriceInfo>
        )}
      </>
    ),
  });
}
